import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { USER_ROLES } from '../../../utils/constants';
import { XCircle, Search, ChevronDown, Check, ArrowLeft } from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import targetService from '../../../api/crm/targets/targetService';
import PermissionGate from '../../../components/common/PermissionGate';
import { API_ENDPOINTS } from '../../../api/config';
import {
  handleTextInputChange,
  handleNumberInputChange,
  validateText,
  validateNumberInput,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from '../../../utils/formValidation';

const TargetBuilder = ({
  targets = [],
  setTargets
}) => {
  const navigate = useNavigate();
  const { userRole } = usePermissions();
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get('id');
  const isEditMode = !!targetId;
  const [targetForm, setTargetForm] = useState({
    period: 'monthly',
    type: 'revenue',
    scope: 'individual',
    value: '',
    assignee: '',
    assignees: [], // for team scope, multiple users
    targetName: '', // User-defined target name (shown for both individual and team)
    startDate: '', // Manual start date selection
    endDate: '', // Manual end date selection for custom period
    baselineRules: ''
  });
  const [periodDates, setPeriodDates] = useState({
    startDate: null,
    endDate: null,
    durationLabel: '',
    expiryInfo: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);
  const MAX_VISIBLE_USERS = 20;
  const assigneeDropdownRef = useRef(null);
  const assigneeInputRef = useRef(null);

  // Value limits for validation
  const VALUE_LIMITS = {
    revenue: { min: 1, max: 1000000000 }, // INR 1 to 1,000,000,000
    count: { min: 1, max: 1000000 } // 1 to 1,000,000 deals/opportunities
  };

  const targetTypes = [
    { id: 'revenue', name: 'Revenue Target', description: 'Total sales revenue target', unit: 'INR' },
    { id: 'deals', name: 'Deal Count', description: 'Number of deals closed', unit: 'COUNT' },
    { id: 'newOpps', name: 'New Opportunities', description: 'New opportunities created', unit: 'COUNT' }
  ];

  // Helper: Get target unit based on target type
  const getTargetUnit = (type) => {
    const targetType = targetTypes.find(t => t.id === type);
    return targetType?.unit || 'COUNT';
  };

  // Helper: Check if target type allows decimals
  const allowsDecimals = (type) => {
    return type === 'revenue';
  };

  // Helper: Get placeholder text based on target type
  const getTargetValuePlaceholder = (type) => {
    switch (type) {
      case 'revenue':
        return 'Enter target revenue amount';
      case 'deals':
        return 'Enter number of deals';
      case 'newOpps':
        return 'Enter number of opportunities';
      default:
        return 'Enter target value';
    }
  };

  // Fetch CRM users for assignee dropdown (same API pattern as ManualLeadEntry)
  const fetchCRMUsers = useCallback(async (searchTerm = '') => {
    setLoadingUsers(true);
    try {
      const trimmed = (searchTerm || '').trim();

      // Build query params for backend search + sensible limits
      const params = new URLSearchParams();
      if (trimmed) {
        params.append('search', trimmed);
      }
      // Use a reasonable limit similar to ManualLeadEntry (50)
      params.append('limit', String(MAX_VISIBLE_USERS * 2));
      // Only fetch sales team members (Sales Manager + Sales Executive) for target assignment
      params.append('roleFilter', 'sales');

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''
        }`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];

        // Transform users to match our format (keep team/department mapping as before)
        const transformedUsers = crmUsers.map(user => ({
          id: user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          department: user.department || '',
          designation: user.designation || '',
          team: user.department || 'Sales Team'
        })).sort((a, b) => a.name.localeCompare(b.name));

        // Merge with existing team members so previously selected users remain available
        setTeamMembers(prev => {
          const map = new Map();
          prev.forEach(u => map.set(u.id, u));
          transformedUsers.forEach(u => map.set(u.id, u));
          return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
        });

        // For the dropdown we only show current search result list
        setFilteredTeamMembers(
          trimmed ? transformedUsers : transformedUsers.slice(0, MAX_VISIBLE_USERS)
        );
      } else {
        setTeamMembers([]);
        setFilteredTeamMembers([]);
      }
    } catch (error) {
      setTeamMembers([]);
      setFilteredTeamMembers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [MAX_VISIBLE_USERS]);

  // Load CRM users on component mount and when search term changes (server-side search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCRMUsers(assigneeSearchTerm);
    }, 400); // debounce for smoother UX

    return () => clearTimeout(timeoutId);
  }, [assigneeSearchTerm, fetchCRMUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        setIsAssigneeDropdownOpen(false);
        setAssigneeSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper: Get today's local date as YYYY-MM-DD string
  const getTodayStr = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Helper: compute end date based on manually selected start date and period
  // startDate = User selected manually, endDate = calculated based on period
  const computePeriodDates = useCallback((period, startDateStr, endDateStr) => {
    // Use manually selected start date, or default to TODAY if not set
    let start;
    if (startDateStr) {
      start = new Date(startDateStr);
      start.setHours(0, 0, 0, 0);
    } else {
      // Default to today if no start date selected
      const today = new Date();
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }

    const year = start.getFullYear();
    const month = start.getMonth();
    const day = start.getDate();

    let end;

    if (period === 'monthly') {
      // MONTHLY: endDate = startDate + 1 month
      end = new Date(year, month + 1, day);
      // Handle edge case: if day doesn't exist in next month (e.g., Jan 31 -> Feb 28/29)
      if (end.getDate() !== day) {
        // Use last day of next month
        end = new Date(year, month + 2, 0);
      }
    } else if (period === 'quarterly') {
      // QUARTERLY: endDate = startDate + 3 months
      end = new Date(year, month + 3, day);
      // Handle edge case: if day doesn't exist in target month
      if (end.getDate() !== day) {
        // Use last day of target month
        end = new Date(year, month + 4, 0);
      }
    } else if (period === 'yearly') {
      // YEARLY: endDate = startDate + 1 year
      end = new Date(year + 1, month, day);
      // Handle leap year edge case: Feb 29 -> Feb 28/29 next year
      if (end.getDate() !== day) {
        // Use last day of target month
        end = new Date(year + 1, month + 1, 0);
      }
    } else if (period === 'custom') {
      // CUSTOM: endDate = use endDateStr if it exists and is after start
      // This is handled partly in UI, but for summary we display the manual selection
      if (startDateStr && !endDateStr) {
        // Default to +1 month if not selected yet
        end = new Date(year, month + 1, day);
        if (end.getDate() !== day) end = new Date(year, month + 2, 0);
      } else if (endDateStr) {
        end = new Date(endDateStr);
        end.setHours(0, 0, 0, 0);
      } else {
        end = new Date(year, month + 1, day);
        if (end.getDate() !== day) end = new Date(year, month + 2, 0);
      }
    } else {
      // Default to monthly
      end = new Date(year, month + 1, day);
      if (end.getDate() !== day) {
        end = new Date(year, month + 2, 0);
      }
    }

    // Format dates: "02 Dec, 2025" format
    const format = (d) => {
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };

    const durationLabel = `${format(start)} – ${format(end)}`;

    // Calculate expiry info based on end date vs today
    const todayOnly = new Date();
    todayOnly.setHours(0, 0, 0, 0);
    const endOnly = new Date(end);
    endOnly.setHours(0, 0, 0, 0);
    const diffMs = endOnly - todayOnly;
    const daysLeft = Math.round(diffMs / (1000 * 60 * 60 * 24));

    let expiryInfo = 'N/A';
    if (daysLeft > 1) {
      expiryInfo = `Expires in ${daysLeft} days`;
    } else if (daysLeft === 1) {
      expiryInfo = 'Expires tomorrow';
    } else if (daysLeft === 0) {
      expiryInfo = 'Expires today';
    } else {
      expiryInfo = 'Expired';
    }

    setPeriodDates({
      startDate: start,
      endDate: end,
      durationLabel,
      expiryInfo
    });
  }, []);

  // Initialize start date with today's date on mount
  useEffect(() => {
    if (!targetForm.startDate) {
      setTargetForm(prev => ({ ...prev, startDate: getTodayStr() }));
    }
  }, [getTodayStr]);

  // Recompute end date when period or start date changes
  useEffect(() => {
    // Always recalculate - function handles empty startDate by defaulting to today
    computePeriodDates(targetForm.period, targetForm.startDate || '', targetForm.endDate || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetForm.period, targetForm.startDate, targetForm.endDate]); // Recalculate when period, startDate or endDate changes

  // Clear value and errors when target type changes (to prevent invalid formats)
  useEffect(() => {
    if (targetForm.value) {
      const currentValue = targetForm.value.toString();
      const allowsDec = allowsDecimals(targetForm.type);

      // If switching from revenue to count-based, remove decimals
      if (!allowsDec && currentValue.includes('.')) {
        const integerValue = currentValue.split('.')[0];
        setTargetForm(prev => ({ ...prev, value: integerValue }));
      }
    }
    // Clear value errors when type changes
    if (errors.value) {
      setErrors(prev => ({ ...prev, value: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetForm.type]);

  const handleSubmitTarget = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    const isTeamScope = targetForm.scope === 'team';

    // Validate required fields (all core fields must be filled)
    const requiredFields = ['period', 'type', 'scope', 'value', 'startDate', 'targetName'];
    if (targetForm.period === 'custom') {
      requiredFields.push('endDate');
    }
    const validation = validateRequiredFields(targetForm, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      alert('Please fill in all required fields');
      return;
    }

    // Custom date range validation
    if (targetForm.period === 'custom') {
      if (targetForm.startDate && targetForm.endDate) {
        if (new Date(targetForm.endDate) <= new Date(targetForm.startDate)) {
          setErrors(prev => ({ ...prev, endDate: 'End date must be after start date' }));
          alert('End date must be after start date');
          return;
        }
      }
    }

    // Target name validation (required for both individual and team, with length limits)
    const targetNameValue = (targetForm.targetName || '').trim();
    if (!targetNameValue) {
      setErrors(prev => ({ ...prev, targetName: 'Target name is required' }));
      alert('Please enter a target name');
      return;
    }

    // Standard security validation: Ensure only allowed symbols are present
    if (targetNameValue && !VALID_TEXT_REGEX.test(targetNameValue)) {
      setErrors(prev => ({ ...prev, targetName: 'Target name contains invalid characters' }));
      alert('Target name contains invalid characters. Only standard symbols are allowed.');
      return;
    }

    const MAX_LENGTH = 50;
    if (targetNameValue.length > MAX_LENGTH) {
      const message = `Target name must not exceed ${MAX_LENGTH} characters`;
      setErrors(prev => ({ ...prev, targetName: message }));
      alert(message);
      return;
    }

    // Baseline rules validation if provided
    if (targetForm.baselineRules && !VALID_TEXT_REGEX.test(targetForm.baselineRules)) {
      setErrors(prev => ({ ...prev, baselineRules: 'Baseline rules contain invalid characters' }));
      alert('Baseline rules contain invalid characters. Only standard symbols are allowed.');
      return;
    }

    // Assignee validation based on scope
    if (isTeamScope) {
      if (!targetForm.assignees || targetForm.assignees.length === 0) {
        setErrors({ assignee: 'Please select at least one team member' });
        alert('Please select at least one team member');
        return;
      }
    } else {
      if (!targetForm.assignee) {
        setErrors({ assignee: 'Please select a valid assignee' });
        alert('Please select an assignee');
        return;
      }
    }

    // Validate target value based on target type
    const targetType = targetForm.type;
    const allowsDec = allowsDecimals(targetType);

    let valueValidation;
    if (allowsDec) {
      // Revenue: Allow decimals but enforce min/max
      const { min, max } = VALUE_LIMITS.revenue;
      valueValidation = validateNumberInput(targetForm.value, max, min);
    } else {
      // Count-based: Only integers allowed
      const numValue = parseFloat(targetForm.value);
      const { min, max } = VALUE_LIMITS.count;
      if (isNaN(numValue)) {
        valueValidation = { isValid: false, error: 'Please enter a valid number' };
      } else if (numValue % 1 !== 0) {
        valueValidation = { isValid: false, error: 'Please enter a whole number (no decimals)' };
      } else if (numValue < min) {
        valueValidation = { isValid: false, error: `Value must be at least ${min}` };
      } else if (numValue > max) {
        valueValidation = { isValid: false, error: `Value must not exceed ${max}` };
      } else {
        valueValidation = { isValid: true };
      }
    }

    if (!valueValidation.isValid) {
      setErrors({ value: valueValidation.error });
      return;
    }

    // Resolve selected assignees
    const selectedIds = isTeamScope ? targetForm.assignees : [targetForm.assignee];
    const selectedMembers = teamMembers.filter(member => selectedIds.includes(member.id));
    if (selectedMembers.length === 0) {
      setErrors({ assignee: 'Please select a valid assignee' });
      return;
    }

    try {
      setLoading(true);

      // Prepare target data according to backend schema
      // Backend expects: name, type, period, scope, value, assignee (required)
      // Optional: assigneeName, assignees, assigneeNames, region, baselineRules, targetUnit

      // Use selected start date or default to today
      const startDateToUse = targetForm.startDate || new Date().toISOString().split('T')[0];

      // Convert YYYY-MM-DD to IST date object at start of day
      // Parse as IST (not UTC) - add IST offset of +5:30
      const [year, month, day] = startDateToUse.split('-').map(Number);
      const istStartDate = new Date(year, month - 1, day, 0, 0, 0, 0);

      // Format as ISO string for backend (backend will store this directly)
      const startDateISO = istStartDate.toISOString();

      // Get target unit based on type
      const targetUnit = getTargetUnit(targetForm.type);

      // Parse value based on type (revenue allows decimals, others are integers)
      const targetValue = allowsDecimals(targetForm.type)
        ? parseFloat(targetForm.value)
        : parseInt(targetForm.value, 10);

      const targetName = targetNameValue || `${targetForm.type} - ${targetForm.period}`;

      const targetData = {
        name: targetName,
        type: targetForm.type,
        period: targetForm.period,
        scope: targetForm.scope,
        value: targetValue,
        targetUnit: targetUnit, // Include target unit (INR or COUNT)
        // Status is now system-driven (computedStatus) - no longer needed from form
        startDate: startDateISO, // Send ISO string with IST date
        endDate: targetForm.period === 'custom' && targetForm.endDate ?
          new Date(new Date(targetForm.endDate).setHours(23, 59, 59)).toISOString() : undefined,
        assignee: selectedIds[0],
        assigneeName: selectedMembers[0]?.name || 'Unknown',
        assignees: isTeamScope ? selectedIds : undefined,
        assigneeNames: isTeamScope ? selectedMembers.map(m => m.name) : undefined,
        region: 'North', // Default region, can be enhanced later
        baselineRules: targetForm.baselineRules || ''
      };

      let result;

      if (isEditMode && targetId) {
        result = await targetService.updateTarget(targetId, targetData);
      } else {
        result = await targetService.createTarget(targetData);
      }


      if (result.success) {
        const savedTarget = result.target;


        // Update local state if setTargets prop is provided
        if (setTargets) {
          setTargets(prev => {
            if (isEditMode && targetId) {
              // Replace existing target in list
              return (prev || []).map(t =>
                t.id === savedTarget.id || t._id === savedTarget.id || t.targetId === savedTarget.targetId
                  ? savedTarget
                  : t
              );
            }
            // Create mode: append new target
            return [...(prev || []), savedTarget];
          });
        }

        // Reset form only in create mode
        if (!isEditMode) {
          setTargetForm(prev => ({
            ...prev,
            period: 'monthly',
            type: 'revenue',
            scope: 'individual',
            value: '',
            assignee: '',
            assignees: [],
            targetName: '',
            startDate: '',
            endDate: '',
            baselineRules: ''
          }));
          computePeriodDates('monthly', '', '');
        }

        // Show success message
        alert(isEditMode ? 'Target updated successfully!' : 'Target created successfully!');

        // Navigate back to Created Targets list
        navigate('/created_targets');
      } else {
        // Extract and display detailed validation errors
        const errorMessage = result.error || (isEditMode ? 'Failed to update target' : 'Failed to create target');
        const validationErrors = result.errors || {};


        // Build detailed error message from errors object
        let detailedMessage = errorMessage;
        const errorKeys = Object.keys(validationErrors);

        if (errorKeys.length > 0) {
          const errorList = errorKeys.map(key => {
            const fieldLabel = key === 'duplicate' ? 'Duplicate Target' :
              key === 'assignees' ? 'Team Members' :
                key === 'assignee' ? 'Assignee' :
                  key === 'value' ? 'Target Value' :
                    key === 'period' ? 'Target Period' :
                      key === 'type' ? 'Target Type' :
                        key === 'scope' ? 'Scope' :
                          key === 'status' ? 'Status' :
                            key === 'startDate' ? 'Start Date' :
                              key.charAt(0).toUpperCase() + key.slice(1);

            const errorValue = validationErrors[key];
            return `• ${fieldLabel}: ${errorValue}`;
          }).join('\n');

          detailedMessage = `${errorMessage}\n\nDetails:\n${errorList}`;
        } else {
          // If no detailed errors, at least show the main error message
          detailedMessage = errorMessage;
        }

        // Show custom alert with all validation errors
        alert(detailedMessage);

        // Also set form-level errors for inline display
        if (errorKeys.length > 0) {
          setErrors(validationErrors);
        }

      }
    } catch (error) {

      // Handle different error types
      let errorMessage = isEditMode
        ? 'Error updating target. Please try again.'
        : 'Error creating target. Please try again.';

      let validationErrors = {};

      if (error.response?.data) {
        errorMessage = error.response.data.responseMessage || errorMessage;
        validationErrors = error.response.data.errors || {};
      } else if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Build detailed error message
      const errorKeys = Object.keys(validationErrors);
      if (errorKeys.length > 0) {
        const errorList = errorKeys.map(key => {
          const fieldLabel = key === 'duplicate' ? 'Duplicate Target' :
            key === 'assignees' ? 'Team Members' :
              key === 'assignee' ? 'Assignee' :
                key === 'value' ? 'Target Value' :
                  key === 'period' ? 'Target Period' :
                    key === 'type' ? 'Target Type' :
                      key === 'scope' ? 'Scope' :
                        key === 'startDate' ? 'Start Date' :
                          key.charAt(0).toUpperCase() + key.slice(1);

          return `• ${fieldLabel}: ${validationErrors[key]}`;
        }).join('\n');

        errorMessage = `${errorMessage}\n\nDetails:\n${errorList}`;
      }

      alert(errorMessage);

      // Set form errors if available
      if (errorKeys.length > 0) {
        setErrors(validationErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load target data in edit mode
  useEffect(() => {
    const loadTarget = async () => {
      if (!isEditMode || !targetId) return;
      try {
        setInitialLoading(true);
        const result = await targetService.getTargetById(targetId);
        if (result.success && result.target) {
          const t = result.target;

          // Pre-populate teamMembers with current assignee(s) to ensure names are displayed
          // This fixes the issue where editing a target showed "Select assignee..." or "Unknown"
          const currentMembers = [];

          // Handle single assignee
          if (t.assigneeInfo) {
            currentMembers.push({
              id: t.assigneeInfo.id || t.assignee,
              name: t.assigneeInfo.name || t.assigneeName || 'Unknown',
              email: t.assigneeInfo.email || '',
              role: t.assigneeInfo.role || '',
              department: '',
              designation: ''
            });
          } else if (t.assignee && t.assigneeName) {
            // Fallback if assigneeInfo object is missing but name exists
            currentMembers.push({
              id: t.assignee,
              name: t.assigneeName,
              email: '',
              role: '',
              department: '',
              designation: ''
            });
          }

          // Handle team assignees
          if (t.assigneesInfo && Array.isArray(t.assigneesInfo)) {
            t.assigneesInfo.forEach(info => {
              if (info.id) {
                currentMembers.push({
                  id: info.id,
                  name: info.name || 'Unknown',
                  email: info.email || '',
                  role: info.role || '',
                  department: '',
                  designation: ''
                });
              }
            });
          }

          if (currentMembers.length > 0) {
            setTeamMembers(prev => {
              const map = new Map();
              prev.forEach(u => map.set(u.id, u));
              currentMembers.forEach(u => {
                if (u.id) map.set(u.id, u);
              });
              return Array.from(map.values());
            });
          }

          setTargetForm(prev => ({
            ...prev,
            period: t.period || 'monthly',
            type: t.type || 'revenue',
            scope: t.scope || 'individual',
            value: t.value != null ? String(t.value) : '',
            // Use assigneeInfo.id if assignee is null (backend may return null for assignee field)
            assignee: t.assignee || t.assigneeInfo?.id || '',
            assignees: Array.isArray(t.assignees) && t.assignees.length ? t.assignees : [],
            targetName: t.name || '',
            // Convert ISO date to YYYY-MM-DD format for date input (IST timezone)
            // Backend stores in UTC, but we need to display in IST
            startDate: t.startDate ? (() => {
              const utcDate = new Date(t.startDate);
              // Convert to IST by adding 5 hours 30 minutes (IST offset)
              const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
              // Format as YYYY-MM-DD
              const year = istDate.getUTCFullYear();
              const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(istDate.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })() : '',
            endDate: t.endDate && t.period === 'custom' ? (() => {
              const utcDate = new Date(t.endDate);
              const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
              const year = istDate.getUTCFullYear();
              const month = String(istDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(istDate.getUTCDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })() : '',
            // Status removed - now system-driven (computedStatus)
            baselineRules: t.baselineRules || ''
          }));

          // Pre-calculate end date for display
          computePeriodDates(t.period || 'monthly', t.startDate || '', t.endDate || '');
        } else {
        }
      } catch (error) {
      } finally {
        setInitialLoading(false);
      }
    };

    loadTarget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, targetId]);

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 mb-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-brand hover:bg-brand/5 rounded-full transition-colors"
              title="Go Back"
              type="button"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Target Builder</h2>
              <p className="text-sm text-gray-500 mt-1">{isEditMode ? 'Modify existing sales target' : 'Create and manage sales targets with ease'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Target Builder Form Container */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Target' : 'Create New Target'}
            </h3>
          </div>

          {initialLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="h-10 w-10 border-4 border-brand border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <form className="space-y-8" onSubmit={handleSubmitTarget} noValidate>
                {/* Target Name - sabse upar for clear identity */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={targetForm.targetName}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        // Apply standard security sanitization (allows all approved symbols)
                        const sanitizedValue = rawValue.replace(INVALID_CHARS_REGEX, '');
                        // Trim leading spaces but allow internal spaces, and hard-limit length
                        const leadingTrimmed = sanitizedValue.replace(/^\s+/, '');
                        const MAX_LENGTH = 50;
                        const finalValue = leadingTrimmed.slice(0, MAX_LENGTH);

                        setTargetForm(prev => ({ ...prev, targetName: finalValue }));
                        if (errors.targetName) {
                          setErrors(prev => ({ ...prev, targetName: '' }));
                        }
                      }}
                      onBlur={(e) => {
                        const trimmed = e.target.value.trim();
                        setTargetForm(prev => ({ ...prev, targetName: trimmed }));
                        if (!trimmed) {
                          setErrors(prev => ({ ...prev, targetName: 'Target name is required' }));
                        } else {
                          const MAX_LENGTH = 50;
                          if (trimmed.length > MAX_LENGTH) {
                            setErrors(prev => ({
                              ...prev,
                              targetName: `Target name must not exceed ${MAX_LENGTH} characters`
                            }));
                          } else {
                            if (errors.targetName) {
                              setErrors(prev => ({ ...prev, targetName: '' }));
                            }
                          }
                        }
                      }}
                      maxLength={50}
                      className={`input-field w-full ${errors.targetName ? 'border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="Enter target name (e.g. Q1 North Revenue Target)"
                    />
                    <div className="flex items-start justify-between mt-1 gap-2">
                      {errors.targetName ? (
                        <p className="text-red-500 text-xs flex-1">{errors.targetName}</p>
                      ) : (
                        <div></div>
                      )}
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {getCharacterCount(targetForm.targetName, 50)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Period <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={targetForm.period}
                      onChange={(e) => {
                        setTargetForm(prev => ({ ...prev, period: e.target.value }));
                        if (e.target.value && errors.period) {
                          setErrors(prev => ({ ...prev, period: '' }));
                        }
                      }}
                      onBlur={(e) => {
                        if (!e.target.value) {
                          setErrors(prev => ({ ...prev, period: 'Target period is required' }));
                        }
                      }}
                      className={`input-field w-full ${errors.period ? 'border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select Period</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                      <option value="custom">Custom Range</option>
                    </select>
                    {errors.period && (
                      <p className="text-red-500 text-xs mt-1">{errors.period}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={targetForm.type}
                      onChange={(e) => {
                        setTargetForm(prev => ({ ...prev, type: e.target.value }));
                        if (e.target.value && errors.type) {
                          setErrors(prev => ({ ...prev, type: '' }));
                        }
                      }}
                      onBlur={(e) => {
                        if (!e.target.value) {
                          setErrors(prev => ({ ...prev, type: 'Target type is required' }));
                        }
                      }}
                      className={`input-field w-full ${errors.type ? 'border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select Type</option>
                      {targetTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-xs mt-1">{errors.type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scope <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={targetForm.scope}
                      onChange={(e) => {
                        setTargetForm(prev => ({ ...prev, scope: e.target.value, assignees: [], assignee: '' }));
                        if (e.target.value && errors.scope) {
                          setErrors(prev => ({ ...prev, scope: '' }));
                        }
                      }}
                      onBlur={(e) => {
                        if (!e.target.value) {
                          setErrors(prev => ({ ...prev, scope: 'Scope is required' }));
                        }
                      }}
                      className={`input-field w-full ${errors.scope ? 'border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select Scope</option>
                      <option value="individual">Individual Rep</option>
                      <option value="team">Team</option>
                    </select>
                    {errors.scope && (
                      <p className="text-red-500 text-xs mt-1">{errors.scope}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Value <span className="text-red-500">*</span> {targetForm.type === 'revenue' && <span className="text-gray-500">(₹)</span>}
                    </label>
                    <div className="relative">
                      {targetForm.type === 'revenue' && (
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                      )}
                      <input
                        type="number"
                        value={targetForm.value}
                        step={allowsDecimals(targetForm.type) ? "0.01" : "1"}
                        min={allowsDecimals(targetForm.type) ? VALUE_LIMITS.revenue.min : VALUE_LIMITS.count.min}
                        max={allowsDecimals(targetForm.type) ? VALUE_LIMITS.revenue.max : VALUE_LIMITS.count.max}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          const targetType = targetForm.type;
                          const allowsDec = allowsDecimals(targetType);
                          const limits = allowsDec ? VALUE_LIMITS.revenue : VALUE_LIMITS.count;

                          // Hard clamp against upper/lower limits immediately
                          let numericValue = inputValue === '' ? '' : Number(inputValue);
                          if (numericValue !== '' && !Number.isNaN(numericValue)) {
                            if (numericValue < limits.min) numericValue = limits.min;
                            if (numericValue > limits.max) numericValue = limits.max;
                          }

                          // For count-based targets, prevent decimal input
                          if (!allowsDec && String(numericValue).includes('.')) {
                            // Remove decimal point for count-based targets
                            const integerValue = String(numericValue).split('.')[0];
                            setTargetForm(prev => ({ ...prev, value: integerValue }));
                          } else {
                            // Use standard number input handler for revenue (allows decimals)
                            const syntheticEvent = {
                              ...e,
                              target: {
                                ...e.target,
                                value: numericValue === '' ? '' : String(numericValue)
                              }
                            };
                            handleNumberInputChange(syntheticEvent, (value) => {
                              setTargetForm(prev => ({ ...prev, value }));
                            }, limits.max, limits.min);
                          }

                          if (errors.value) {
                            setErrors(prev => ({ ...prev, value: '' }));
                          }
                        }}
                        onBlur={(e) => {
                          if (!targetForm.value) {
                            setErrors(prev => ({ ...prev, value: 'Target value is required' }));
                          }
                        }}
                        className={`input-field w-full ${errors.value ? 'border-red-500 focus:ring-red-500' : ''} ${targetForm.type === 'revenue' ? 'pl-8' : ''}`}
                        placeholder={getTargetValuePlaceholder(targetForm.type)}
                        required
                      />
                    </div>
                    {errors.value && (
                      <p className="text-red-500 text-xs mt-1">{errors.value}</p>
                    )}
                    {!errors.value && targetForm.type === 'revenue' && (
                      <p className="text-gray-500 text-xs mt-1">Enter amount in Indian Rupees (₹)</p>
                    )}
                    {!errors.value && (targetForm.type === 'deals' || targetForm.type === 'newOpps') && (
                      <p className="text-gray-500 text-xs mt-1">Enter whole number only (no decimals)</p>
                    )}
                  </div>
                </div>

                {/* (Legacy Team Name block removed; Target Name is now used above for both scopes) */}

                {/* Start Date + Period summary row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={targetForm.startDate}
                      min={getTodayStr()}
                      disabled={isEditMode && targetForm.startDate && targetForm.startDate < getTodayStr()}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        const today = getTodayStr();

                        // Block past date selection
                        if (selectedDate < today) {
                          return;
                        }

                        setTargetForm(prev => ({ ...prev, startDate: selectedDate }));
                        if (errors.startDate) {
                          setErrors(prev => ({ ...prev, startDate: '' }));
                        }
                      }}
                      onClick={(e) => {
                        try {
                          if (e.target.showPicker) {
                            e.target.showPicker();
                          }
                        } catch (err) {
                          // Silently catch - some browsers are strict about user gestures
                        }
                      }}
                      onBlur={() => {
                        if (!targetForm.startDate) {
                          setErrors(prev => ({ ...prev, startDate: 'Start date is required' }));
                        }
                      }}
                      className={`input-field w-full ${errors.startDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {errors.startDate && (
                      <p className="text-red-500 text-xs mt-1">{errors.startDate}</p>
                    )}
                  </div>

                  {targetForm.period === 'custom' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={targetForm.endDate}
                        min={(() => {
                          const today = getTodayStr();
                          if (!targetForm.startDate) return today;
                          return targetForm.startDate > today ? targetForm.startDate : today;
                        })()}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          const today = getTodayStr();
                          const minDate = targetForm.startDate && targetForm.startDate > today ? targetForm.startDate : today;

                          if (selectedDate < minDate) {
                            return; // Block invalid end date selection
                          }

                          setTargetForm(prev => ({ ...prev, endDate: selectedDate }));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: '' }));
                          }
                        }}
                        onClick={(e) => {
                          try {
                            if (e.target.showPicker) {
                              e.target.showPicker();
                            }
                          } catch (err) { }
                        }}
                        onBlur={() => {
                          if (!targetForm.endDate) {
                            setErrors(prev => ({ ...prev, endDate: 'End date is required' }));
                          }
                        }}
                        className={`input-field w-full ${errors.endDate ? 'border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {errors.endDate && (
                        <p className="text-red-500 text-xs mt-1">{errors.endDate}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Target Duration (End Date)
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm text-gray-900">
                        {periodDates.durationLabel || 'Auto-calculated based on start date and period'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Assignee row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div className="relative" ref={assigneeDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assignee <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen);
                          if (!isAssigneeDropdownOpen) {
                            setTimeout(() => {
                              assigneeInputRef.current?.focus();
                            }, 100);
                          }
                        }}
                        onBlur={() => {
                          // Trigger validation if no member is selected
                          const isTeam = targetForm.scope === 'team';
                          if (isTeam) {
                            if (!targetForm.assignees || targetForm.assignees.length === 0) {
                              setErrors(prev => ({ ...prev, assignee: 'Please select team members' }));
                            }
                          } else {
                            if (!targetForm.assignee) {
                              setErrors(prev => ({ ...prev, assignee: 'Please select an assignee' }));
                            }
                          }
                        }}
                        className={`input-field w-full bg-white flex items-center justify-between ${errors.assignee ? 'border-red-500 focus:ring-red-500' : ''
                          } ${loadingUsers ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        disabled={loadingUsers}
                      >
                        <span className={(targetForm.assignee || (targetForm.assignees && targetForm.assignees.length > 0)) ? 'text-gray-900' : 'text-gray-500'}>
                          {targetForm.scope === 'team'
                            ? (targetForm.assignees && targetForm.assignees.length > 0
                              ? `${targetForm.assignees.length} member(s) selected`
                              : (loadingUsers ? 'Loading users...' : 'Select team members...'))
                            : (targetForm.assignee
                              ? teamMembers.find(m => m.id === targetForm.assignee)?.name || 'Select assignee...'
                              : (loadingUsers ? 'Loading users...' : 'Select assignee...'))}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isAssigneeDropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown */}
                      {isAssigneeDropdownOpen && (
                        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  ref={assigneeInputRef}
                                  type="text"
                                  value={assigneeSearchTerm}
                                  onChange={(e) => {
                                    // Apply standard security sanitization (allows all approved symbols including email chars)
                                    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                                    setAssigneeSearchTerm(value);
                                  }}
                                  placeholder="Search users..."
                                  maxLength={100}
                                  className="input-field w-full pl-8 pr-3 py-2 text-sm"
                                  autoFocus
                                />
                              </div>
                              <div className="flex justify-end mt-1">
                                <span className="text-xs text-gray-500">
                                  {getCharacterCount(assigneeSearchTerm, 100)}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-[240px]">
                            {loadingUsers && filteredTeamMembers.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                Loading CRM team members...
                              </div>
                            ) : filteredTeamMembers.length === 0 ? (
                              <div className="p-3 text-sm text-gray-500 text-center">
                                {assigneeSearchTerm ? 'No users found' : 'No CRM team members available'}
                              </div>
                            ) : (
                              (assigneeSearchTerm.trim() === ''
                                ? filteredTeamMembers.slice(0, MAX_VISIBLE_USERS)
                                : filteredTeamMembers
                              ).map((member) => {
                                const isSelectedIndividual = targetForm.assignee === member.id;
                                const isSelectedTeam = targetForm.assignees?.includes(member.id);
                                const isSelected = targetForm.scope === 'team' ? isSelectedTeam : isSelectedIndividual;

                                return (
                                  <button
                                    key={member.id}
                                    type="button"
                                    onClick={() => {
                                      if (targetForm.scope === 'team') {
                                        setTargetForm(prev => {
                                          const current = prev.assignees || [];
                                          const exists = current.includes(member.id);
                                          const nextAssignees = exists
                                            ? current.filter(id => id !== member.id)
                                            : [...current, member.id];
                                          return { ...prev, assignees: nextAssignees };
                                        });
                                        if (errors.assignee) {
                                          setErrors(prev => ({ ...prev, assignee: '' }));
                                        }
                                      } else {
                                        setTargetForm(prev => ({ ...prev, assignee: member.id }));
                                        setIsAssigneeDropdownOpen(false);
                                        setAssigneeSearchTerm('');
                                        if (errors.assignee) {
                                          setErrors(prev => ({ ...prev, assignee: '' }));
                                        }
                                      }
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${isSelected ? 'bg-brand/5 font-medium' : 'text-gray-700'
                                      }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-start gap-2">
                                        {targetForm.scope === 'team' ? (
                                          <input
                                            type="checkbox"
                                            readOnly
                                            checked={isSelectedTeam}
                                            className="mt-0.5 h-4 w-4 text-brand border-gray-300 rounded"
                                          />
                                        ) : (
                                          isSelectedIndividual && (
                                            <Check className="mt-0.5 h-4 w-4 text-brand" />
                                          )
                                        )}
                                        <div>
                                          <div className="font-medium">{member.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {member.email} {member.designation ? `• ${member.designation}` : (member.department ? `• ${member.department}` : '')}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Selected team members (for Team scope) */}
                {targetForm.scope === 'team' && targetForm.assignees && targetForm.assignees.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {targetForm.assignees.map((id) => {
                      const member = teamMembers.find(m => m.id === id);
                      if (!member) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center max-w-full px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                        >
                          <span className="truncate">
                            {member.name || 'Unknown'}
                            {member.email ? ` • ${member.email}` : ''}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const nextAssignees = (targetForm.assignees || []).filter(
                                assigneeId => assigneeId !== id
                              );
                              setTargetForm(prev => ({
                                ...prev,
                                assignees: nextAssignees
                              }));
                              if (errors.assignee && nextAssignees.length > 0) {
                                setErrors(prevErr => ({ ...prevErr, assignee: '' }));
                              }
                            }}
                            className="ml-1 text-gray-400 hover:text-red-500 focus:outline-none"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {errors.assignee && (
                  <p className="text-red-500 text-xs mt-1">{errors.assignee}</p>
                )}
                {!loadingUsers && teamMembers.length === 0 && !isAssigneeDropdownOpen && (
                  <p className="text-xs text-yellow-600 mt-1">No CRM team members found</p>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Baseline Rules
                  </label>
                  <textarea
                    value={targetForm.baselineRules}
                    onChange={(e) => {
                      handleTextInputChange(e, (value) => {
                        setTargetForm(prev => ({ ...prev, baselineRules: value }));
                      }, FIELD_LIMITS.notes);
                    }}
                    className="input-field w-full py-2"
                    rows={4}
                    placeholder="Enter baseline calculation rules and carry-over policies..."
                  ></textarea>
                  <div className="flex justify-end mt-1">
                    <span className="text-xs text-gray-500">
                      {getCharacterCount(targetForm.baselineRules, FIELD_LIMITS.notes)}
                    </span>
                  </div>
                </div>

                {/* Button section */}
                <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="btn-cancel px-6 py-2"
                  >
                    Cancel
                  </button>
                  {userRole !== USER_ROLES.SALES_EXECUTIVE && (
                    <PermissionGate permission="target_achievement" action={isEditMode ? "update" : "create"}>
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-create px-8 py-2 min-w-[140px]"
                      >
                        {loading
                          ? (isEditMode ? 'Updating...' : 'Creating...')
                          : (isEditMode ? 'Update Target' : 'Create Target')}
                      </button>
                    </PermissionGate>
                  )}
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TargetBuilder;
